import i18n from 'services/i18n'
import moment from 'moment'
import Big from 'big.js'

import { parseCurrency, getCurrencySymbolFromCode } from 'helpers/currency'
import {
  formatDateAxisMonthDayFull,
  getDateRange,
  groupDataByMonth,
  groupDataByWeek,
  groupDataByWeekAndTrim
} from 'helpers/charts'
import {
  humanizeLongNumber,
  humanizeLongNumberWithLocale,
  percentageFormatter
} from 'helpers/formatters'
import { sumKeys, avgKeys, avgRating, avgKeysCurrency } from 'helpers/objects'
import { CHART_TOOLTIP_CONFIG } from 'constants/competitive_intelligence/common'

export const getAxisLabel = metric => {
  switch (metric) {
    case 'market_share':
      return i18n.t('common:MetricName.marketShare', 'Market Share')
    case 'revenue':
      return i18n.t('common:MetricName.revenue', 'Revenue')
    case 'units_sold':
      return i18n.t('common:MetricName.unitsSold', 'Units Sold')
    case 'avg_price':
      return i18n.t('common:MetricName.avgMedianPrice', 'Avg Median Price')
    case 'star_rating':
      return i18n.t('common:MetricName.starRating', 'Star Rating')
    case 'reviews':
      return i18n.t('common:MetricName.reviews', 'Reviews')
    default:
      return i18n.t('common:MetricName.revenue', 'Revenue')
  }
}

export const getChartTitle = metric => {
  switch (metric) {
    case 'revenue':
      return i18n.t(
        'competitive_intelligence:OverviewTab.revenueOverTime',
        'Revenue Over Time'
      )
    case 'units_sold':
      return i18n.t(
        'competitive_intelligence:OverviewTab.unitsSoldOverTime',
        'Units Sold Over Time'
      )
    case 'avg_price':
      return i18n.t(
        'competitive_intelligence:OverviewTab.avgMedianPriceOverTime',
        'Avg Median Price Over Time'
      )
    case 'star_rating':
      return i18n.t(
        'competitive_intelligence:OverviewTab.starRatingOverTime',
        'Star Rating Over Time'
      )
    case 'reviews':
      return i18n.t(
        'competitive_intelligence:OverviewTab.reviewsOverTime',
        'Reviews Over Time'
      )
    case 'market_share':
      return i18n.t(
        'competitive_intelligence:OverviewTab.marketShareOverTime',
        'Market Share Over Time'
      )
    default:
      return i18n.t(
        'competitive_intelligence:OverviewTab.revenueOverTime',
        'Revenue Over Time'
      )
  }
}

const safeBig = value => {
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    isNaN(value) ||
    !isFinite(value)
  ) {
    return Big(0) // Default to 0 if the value is invalid
  }
  return Big(value) // Proceed with valid values
}

export const metricFormatter = (value, metric) => {
  const bigValue = safeBig(value)

  if (metric === 'revenue') {
    return parseCurrency(bigValue.toFixed(0), 'USD', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
  }

  if (metric === 'avg_price') {
    return parseCurrency(bigValue.toFixed(2), 'USD', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  if (metric === 'star_rating') {
    return humanizeLongNumberWithLocale(bigValue.toFixed(1))
  }

  if (metric === 'market_share') {
    return percentageFormatter(bigValue.times(100).toFixed(2))
  }

  return humanizeLongNumberWithLocale(
    bigValue.round(0, Big.roundHalfUp).toNumber()
  )
}

export const metricYAxisFormatter = (value, metric) => {
  const formatted = `${humanizeLongNumber(value)}`.toUpperCase()

  if (['revenue', 'avg_price'].includes(metric)) {
    return `${getCurrencySymbolFromCode()}${formatted}`
  }

  if (['units_sold', 'reviews'].includes(metric)) {
    return formatted
  }

  return parseFloat(value).toFixed(0)
}

export const formatTrendChartData = ({ data, interval }) => {
  if (!data?.length) return []

  const groupedData =
    interval === 'weekly'
      ? groupDataByWeek(data, 'x')
      : groupDataByMonth(data, 'x')
  const forcedInterval = interval === 'weekly' ? 'monthly' : null

  return Object.values(groupedData).map(group => {
    const dateRange = getDateRange({ data: group, interval, forcedInterval })
    const result = {
      ...dateRange,
      ...sumKeys(group, ['revenue', 'units_sold']),
      ...avgKeys(group, ['market_share'], true)
    }

    return result
  })
}

export const formatChartData = ({ data, interval, trim = false }) => {
  if (!data || data.length === 0) return []

  if (interval === 'daily') {
    return data.map(item => {
      const formattedDate = formatDateAxisMonthDayFull(item.date)
      return {
        ...item,
        formattedDate
      }
    })
  }

  let groupedData = {}

  if (interval === 'weekly') {
    groupedData = trim
      ? groupDataByWeekAndTrim(data, 'x')
      : groupDataByWeek(data, 'x')
  } else if (interval === 'monthly') {
    groupedData = groupDataByMonth(data, 'x')
  }

  const dataKeys = Object.keys(data[0]).filter(key => key !== 'date') // ['avg_price', 'revenue', 'reviews', 'star_rating', 'units_sold', 'market_share']

  const keysToSum = []
  const keysToAvg = []
  const keysToAvgCurrency = []
  const keysToAvgRating = []

  dataKeys.forEach(key => {
    const metric = key.split('-')[0]

    switch (metric) {
      case 'revenue':
      case 'units_sold':
        keysToSum.push(key)
        break
      case 'reviews':
      case 'market_share':
        keysToAvg.push(key)
        break
      case 'avg_price':
        keysToAvgCurrency.push(key)
        break
      case 'star_rating':
        keysToAvgRating.push(key)
        break
      default:
        break
    }
  })

  // Use safeBig and Big.js methods for arithmetic
  return Object.values(groupedData).map(group => {
    const dateRange = getDateRange({ data: group, interval })

    const mergedGroup = {
      ...sumKeys(
        group.map(item =>
          Object.fromEntries(
            keysToSum.map(key => [key, safeBig(item[key]).toNumber()])
          )
        ),
        keysToSum
      ),
      ...avgKeys(
        group.map(item =>
          Object.fromEntries(
            keysToAvg.map(key => [key, safeBig(item[key]).toNumber()])
          )
        ),
        keysToAvg,
        true
      ),
      ...avgKeysCurrency(
        group.map(item =>
          Object.fromEntries(
            keysToAvgCurrency.map(key => [key, safeBig(item[key]).toNumber()])
          )
        ),
        keysToAvgCurrency,
        true
      ),
      ...avgRating(
        group.map(item =>
          Object.fromEntries(
            keysToAvgRating.map(key => [key, safeBig(item[key]).toNumber()])
          )
        ),
        keysToAvgRating,
        true
      )
    }

    return {
      ...dateRange,
      ...mergedGroup
    }
  })
}

const findMedian = arr => {
  const sortedArr = arr.slice().sort((a, b) => a - b)
  const middleIndex = Math.floor(sortedArr.length / 2)

  if (sortedArr.length % 2 === 0) {
    return (sortedArr[middleIndex - 1] + sortedArr[middleIndex]) / 2
  }

  return sortedArr[middleIndex]
}

export const calculateGroupMedians = data => {
  const revenue = data.map(item => item.revenue)
  const market_share = data.map(item => item.market_share)
  const units_sold = data.map(item => item.units_sold)
  const avg_price = data.map(item => item.avg_price)
  const star_rating = data.map(item => item.star_rating)
  const reviews = data.map(item => item.reviews)

  return {
    revenue: findMedian(revenue),
    market_share: findMedian(market_share),
    units_sold: Math.round(findMedian(units_sold)),
    avg_price: findMedian(avg_price),
    star_rating: findMedian(star_rating),
    reviews: Math.round(findMedian(reviews))
  }
}

export const emptyStateMessage = (filter, competitorsCount) => {
  if (competitorsCount === 0 && filter !== 'all') {
    return i18n.t(
      'competitive_intelligence:KeywordAnalysis.emptyState.noCompetitor',
      'Select at least one competitor to compare product keyword rankings.'
    )
  }

  if (competitorsCount >= 1 && competitorsCount <= 2) {
    return i18n.t(
      'competitive_intelligence:KeywordAnalysis.emptyState.sharedKeywords',
      'Choose competitors that are relevant to your product - they’re likely to share common keywords!'
    )
  }

  if (competitorsCount >= 4 && competitorsCount <= 6 && filter !== 'all') {
    return i18n.t(
      'competitive_intelligence:KeywordAnalysis.emptyState.optimalSelection',
      'For optimal results, try narrowing down your selection of competitors!'
    )
  }

  return i18n.t(
    'generic:EmptyState.noResults.description',
    'Please change your filter criteria and try again'
  )
}

export const getMinMaxDatesCalendar = () => {
  const maxDate = moment()
    .subtract(1, 'week')
    .endOf('week')
    .startOf('day')

  const minDate = maxDate
    .clone()
    .subtract(365, 'days')
    .toDate()

  return { minDate, maxDate: maxDate.toDate() }
}

export const formatDate = value => {
  const date = moment(value)

  if (value === undefined || value === null || !date.isValid()) {
    return '-'
  }
  return date.format('MMMM DD, YYYY')
}

export const isChartTooltipExceedingPage = ({ left }) => {
  const pageBuffer = 20

  return (
    left + CHART_TOOLTIP_CONFIG.width + CHART_TOOLTIP_CONFIG.offset >
    window.innerWidth - pageBuffer
  )
}

export const unavailableProductMock = ({ asin, marketplace }) => {
  return {
    imageUrl: null,
    asin,
    category: '-',
    brand: '-',
    isParent: false,
    scrapedParentAsin: '-',
    id: `${marketplace}/${asin}`,
    name: 'Unavailable Product',
    variantAsinsCount: 0,
    categoryCode: '-',
    is_supported_category: false,
    error: 'unavailable_product'
  }
}

export const isUpgradeIncentiveEligible = ({
  billingFrequency,
  status,
  subscriptionDate,
  planId
}) => {
  return (
    billingFrequency === 'monthly' &&
    status === 'active' &&
    moment
      .utc(subscriptionDate)
      .isBetween('2024-01-01', '2024-11-01', 'day', '[)') &&
    [743, 700].includes(planId)
  )
}
